// Mock data for Shweta Chauhan — Product Designer Portfolio
// Pure frontend mock data (no backend).

export const navLinks = [
  { label: "Work", href: "/#work" },
  { label: "My Approach", href: "/#process" },
  { label: "About", href: "/#about" },
  { label: "Contact", href: "/#contact" },
];

export const trustLogos = [
  "Health-tech",
  "Fintech",
  "E-commerce",
  "AI Platforms",
  "SaaS",
];

export const whyHireMe = [
  {
    icon: "Compass",
    title: "I simplify ambiguity into clear user flows",
    body: "I take fuzzy product problems — half-formed requirements, conflicting stakeholder asks — and turn them into decisions, flows, and screens teams can actually ship.",
  },
  {
    icon: "Network",
    title: "I think in systems, not just screens",
    body: "Components, states, edge cases, and growth loops. I design for the second screen, the third user, and the version six months from now.",
  },
  {
    icon: "Target",
    title: "I balance user needs with product goals",
    body: "Beautiful UI is the easy part. The harder craft is aligning what users want, what the business needs, and what engineering can build — without compromise.",
  },
];

const _caseStudiesRaw = [
  {
    id: "calmkraft",
    name: "Calm Kraft Botanics",
    category: "E-commerce · D2C wellness",
    tag: "E-commerce",
    year: "2025",
    role: "Product Designer",
    duration: "2 months",
    team: "",
    platform: "Web (D2C storefront)",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/uyziqko7_CKB%20Hero%20Image.png",
    imagePosition: "center",
    screenAspect: "laptop",
    screens: [
      {
        label: "Newsletter capture",
        caption: "Welcome modal layered over a warm hero — 20% incentive to opt in",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/26emtuff_image%205.png",
      },
      {
        label: "Our Fine Products",
        caption: "Scannable Honey + Turmeric cards with quantity, Add to Cart and Buy Now",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/tvn9ko8m_image%2013.png",
      },
      {
        label: "Who We Are & What We Do",
        caption: "Trust signals — authenticity, secure payments, express delivery",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/v16917hl_image%2011.png",
      },
      {
        label: "Our Processing Method",
        caption: "Sourcing → Processing → Packaging → Delivery — claim turned into a visible workflow",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/zkenrbap_image%2010.png",
      },
      {
        label: "Frequently Asked Questions",
        caption: "Buying objections answered upfront — bulk, returns, sourcing, shipping, quality",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/jzmwug5f_image%209.png",
      },
      {
        label: "Our Story",
        caption: "Founder narrative chapters — humble beginnings, values, today",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/uabwgztf_image%208.png",
      },
    ],
    description:
      "A D2C wellness storefront for premium honey and turmeric — designed to bridge the trust gap between organic sourcing and digital shoppers through warm storytelling, clear hierarchy and frictionless purchase flows.",
    headline:
      "Bridging the trust gap between organic sourcing and digital shoppers.",
    challenge:
      "Many natural wellness brands have strong products but weak digital trust. Buyers hesitate online because they can’t inspect quality, authenticity or sourcing — and most storefronts don’t close that gap.",
    approach:
      "A product-thinking approach built on visual credibility, product clarity, frictionless navigation and a conversion-ready hierarchy — pairing storytelling with commerce so the emotional and transactional moments don’t fight each other.",
    outcome:
      "A premium, credible storefront that turns brand warmth into purchase confidence — clear product cards, an explicit processing method, a founder’s story, and an FAQ that answers buying objections before they appear.",
    context:
      "Calm Kraft Botanics is a D2C e-commerce site for premium natural wellness products — primarily honey and turmeric. The brand had strong physical products but needed a storefront that felt authentic, premium and easy to shop, designed to win trust before the cart.",
    problemLabel: "Project Overview",
    problem: [
      "Many natural wellness brands have strong products, but weak digital trust.",
      "Consumers often hesitate when buying health-related products online because they cannot physically inspect quality, authenticity, or sourcing.",
      "The brand needed a website that showcases its natural, authentic products in a visually appealing way — while bridging the trust gap between organic sourcing and digital consumers.",
    ],
    process: [
      {
        title: "Trust-driven homepage",
        body: "A premium first impression that quickly communicates product quality, natural ingredients and brand warmth. Clear value proposition above the fold, immediate trust signals (sourcing, ratings, delivery), and a layered welcome offer to capture intent without blocking browsing.",
      },
      {
        title: "Why Choose Us · brand values",
        body: "A section that pairs a human face with product visuals to create warmth and credibility. Authenticity, freshness and careful delivery are presented as design statements, not bullet points — so buyers feel the values, not just read them.",
      },
      {
        title: "Processing method · sourcing → delivery",
        body: "Instead of claiming quality, the brand shows it. A four-step transparent flow: 01 Sourcing from trusted farmers · 02 Processing with traditional + modern methods · 03 Eco-friendly packaging · 04 Careful delivery — turning a vague promise into a visible workflow.",
      },
      {
        title: "Founder story + FAQ",
        body: "A multi-chapter founder’s story that creates a personal bridge between customer and brand — humble beginnings, mission, today. Paired with an FAQ that proactively answers ingredient, sourcing, shipping and quality questions, killing hesitation right before the buy.",
      },
    ],
    metrics: [
      { label: "Brand perception", before: "generic wellness", after: "premium D2C" },
      { label: "Sourcing claim", before: "told", after: "shown" },
      { label: "Discovery", before: "high friction", after: "scannable" },
      { label: "Buying confidence", before: "hesitation", after: "decided" },
    ],
    learnings: [
      "Storytelling must support action — brand narrative should lead users closer to purchase, not away from it.",
      "Simplicity sells. The easier something feels to shop, the more likely users actually convert.",
      "Visual design influences value perception. Premium presentation directly increases willingness to buy.",
    ],
  },
  {
    id: "scijustice",
    name: "SciJustice",
    category: "Legal-tech · Expert consultation",
    tag: "Legal-tech",
    year: "2025",
    role: "UX/UI Designer",
    duration: "8 weeks",
    team: "",
    platform: "Web platform",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/0h8dpoj8_image%201.png",
    imagePosition: "top",
    screenAspect: "laptop",
    screens: [
      {
        label: "Judge dashboard",
        caption: "At-a-glance overview + clear next best action",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/0h8dpoj8_image%201.png",
      },
      {
        label: "Inquiry details",
        caption: "Natural-language intake the AI can later structure",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/94pqrxgp_image%203.png",
      },
      {
        label: "Expertise selection",
        caption: "Field, subfield and time-commitment filters",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/mlomweuj_image%204.png",
      },
      {
        label: "Compliance checkpoint",
        caption: "Boundaries reviewed before any request goes out",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/0wz42luj_image%205.png",
      },
      {
        label: "Generated request review",
        caption: "AI summarises the inputs into a structured request profile",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/r7ul0g95_image%206.png",
      },
      {
        label: "Expert match results",
        caption: "Match scores explain why each expert was recommended",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/nzryaftc_image%207.png",
      },
      {
        label: "Request sent · locked session",
        caption: "Affidavit-gated session before any chat begins",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/m116jt5y_image%209.png",
      },
      {
        label: "Expert dashboard",
        caption: "Pending requests, ongoing chats and summary tasks in one view",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/j3hgbfcn_image%2011.png",
      },
      {
        label: "Consultation request review",
        caption: "Conflict-of-interest reminder, request details, accept / reject / hold",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/3hzeffyq_image%2012.png",
      },
    ],
    description:
      "A secure, transparent system that lets judges consult subject-matter experts — doctors, engineers, financial analysts, forensic specialists — without compromising independence or compliance.",
    headline:
      "Bringing safe, structured expert consultation into judicial workflows.",
    challenge:
      "Judges had no safe, structured way to consult external subject-matter experts without risking conflicts of interest or compromising judicial independence.",
    approach:
      "A step-based wizard with explicit compliance checkpoints, clear status indicators, and a neutral minimalist UI — designed to feel dependable, transparent and operationally efficient.",
    outcome: "",
    context:
      "Many legal decisions need domain experts. Courts often need specialists to support complex cases. SciJustice modernises this through a secure digital platform built on four design goals: faster expert matching, compliance at every step, secure communication, and efficient management across multiple active cases.",
    problemLabel: "Project Overview",
    problem: [
      "Many legal decisions require domain experts.",
      "Courts often need specialists such as doctors, engineers, financial analysts or forensic professionals to support complex cases.",
      "SciJustice was designed to modernize this process through a secure, transparent digital platform.",
    ],
    designGoals: [
      {
        title: "Faster Expert Matching",
        body: "Help judges find the right expert quickly — match score, credentials and availability surfaced together.",
      },
      {
        title: "Compliance",
        body: "Ensure compliance at every step with built-in safeguards and explicit checkpoints before sensitive actions.",
      },
      {
        title: "Secure Communication",
        body: "Enable trusted conversations and document exchange inside affidavit-gated sessions.",
      },
      {
        title: "Efficient Management",
        body: "Simplify coordination across multiple active cases with clear status and a single dashboard.",
      },
    ],
    process: [
      {
        title: "Dashboard for the judge",
        body: "An at-a-glance overview of ongoing requests, completed consultations and expert activity. Designed to reduce cognitive load, prioritising the next best action — start a new expert request — over information density.",
      },
      {
        title: "Guided request creation",
        body: "A 4-step wizard: inquiry details (natural-language intake the AI later structures), expertise selection (field, subfield, time commitment), compliance checkpoint, and a review screen with an AI summary the judge can edit before submitting.",
      },
      {
        title: "Expert discovery & selection",
        body: "Match results explain why each expert was recommended — match score, credentials, availability — instead of dumping random profiles. CV details open in a focused modal so judges can validate experience without losing context.",
      },
      {
        title: "Secure communication & finalisation",
        body: "Locked sessions with required affidavits before chat begins, a structured consultation chat that reinforces boundaries around case-specific information, and a session summary with downloadable records to create a reliable audit trail.",
      },
    ],
    metrics: [],
    outcomeHighlights: [
      "Step-based wizard approach",
      "Explicit compliance checkpoints",
      "Clear status indicators",
      "Neutral, minimalist design",
    ],
    learnings: [
      "In high-stakes systems, clarity isn’t a convenience — it’s essential.",
      "Trust signals (status, compliance, attribution) are first-class UI, not microcopy.",
      "Step-based wizards beat single long forms when the cost of a wrong submission is high.",
    ],
  },
  {
    id: "sparkminds",
    name: "SparkMinds",
    category: "AI · Child development",
    tag: "Consumer AI",
    year: "2026",
    role: "Lead Product Designer",
    duration: "4 months",
    team: "1 Designer, 2 Developers, 1 PM",
    platform: "iOS, Android, Web",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/53uhqe38_SparkMinds%20MockUp%20%281%29.png",
    imagePosition: "center",
    screenAspect: "phone-framed",
    screens: [
      {
        label: "Profile intake — Step 2 of 5",
        caption:
          "One question at a time, with a calm progress bar — no overwhelming form.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/gyqermvr_1.png",
      },
      {
        label: "Hearing profile — Step 3 of 5",
        caption:
          "Adaptive multi-select that shapes the AI’s recommendations downstream.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/n26q6vw6_2.png",
      },
      {
        label: "AI-suggested goals",
        caption:
          "The model proposes 5 developmental goals tuned to the child’s stage — parents pick what matters.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/3hrwviks_3.png",
      },
      {
        label: "You’re all set",
        caption:
          "A warm completion moment — onboarding ends with delight, not a dead-end.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/d7y40pkr_4.png",
      },
      {
        label: "Daily activity",
        caption:
          "The personalised activity feed — what the model learned, turned into a hands-on 30-minute moment.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/fttf3ynq_5.png",
      },
    ],
    description:
      "SparkMinds is a personalized learning companion for parents of young children, designed to support language and developmental growth through simple, everyday activities. By combining expert-backed frameworks with AI-driven recommendations, the app helps parents turn daily moments into meaningful learning experiences — building skills consistently, one step at a time.",
    headline: "AI-powered Parent-Guided Child Development Platform",
    challenge:
      "Parents needed personalised activity guidance from an AI that understood their child — but the data input felt like a survey from hell.",
    approach:
      "Designed a conversational onboarding that captured 40+ signals across 90 seconds, then a daily ‘next best activity’ feed driven by the model.",
    outcome: "",
    context:
      "SparkMinds is a personalized learning companion for parents of young children, designed to support language and developmental growth through simple, everyday activities. By combining expert-backed frameworks with AI-driven recommendations, the app helps parents turn daily moments into meaningful learning experiences — building skills consistently, one step at a time.",
    problem: [],
    process: [
      {
        title: "Understanding the System",
        body: "Instead of jumping into screens, I mapped the system: Parent input → AI recommendations → Daily actions → Feedback. This helped me understand how logic and user experience connect.",
      },
      {
        title: "Designing Key Flows",
        body: "I focused on designing a few critical flows first: Onboarding → Goal Recommendation → Selection · Daily Activity → Guided Execution → Feedback · Progress Tracking → Reflection → Next Steps. The goal was to keep each flow simple, intentional and low-effort for parents.",
      },
      {
        title: "Balancing Simplicity & Depth",
        body: "1 activity per day instead of weekly plans. Guided steps instead of long instructions — depth on demand, not overwhelm by default.",
      },
      {
        title: "Designing Around AI (Not Just With AI)",
        body: "AI wasn’t just a feature — it influenced the entire experience. I designed goal recommendations based on user input, adaptive activities based on feedback, and flexible flows where parents can adjust or regenerate. The goal was to make AI feel helpful, not overwhelming or invisible.",
      },
      {
        title: "Iterating Through Feedback",
        body: "Throughout the process, I continuously shared early concepts, refined flows based on feedback, and simplified wherever friction appeared. This helped move from idea → clarity → usability.",
      },
    ],
    metrics: [],
    learnings: [
      "For AI products, the onboarding *is* the model’s training data — design it like a feature, not a form.",
      "Showing the user what the system is learning in real time is the single highest-leverage trust mechanic.",
    ],
    founderQuote: {
      label: "What the Product Founder said about the design",
      quote:
        "LOVE LOVE LOVE the UI/UX vibe. Every time I see it, I feel calm and supported. SparkMinds as an “expert companion” comes through for me clearly. I can’t wait to share it more broadly and get feedback.",
      name: "Uma Soman",
      role: "Founder, SparkMinds",
    },
  },
  {
    id: "satvikscan",
    name: "SatvikScan",
    category: "AI · Dietary compliance",
    tag: "Consumer AI",
    year: "2025",
    role: "UX/UI Designer",
    duration: "4 months",
    team: "",
    platform: "iOS, Android",
    summary: "AI Dietary Compliance App",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/vo6783wz_Scanning%20food%20ingredients%20with%20smartphone%20app.png",
    sketches: [
      {
        label: "Overall flow",
        caption: "Scan → Extract → Analyse → Results → Save History",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/n7m07u1g_20260101_230828%201.png",
      },
      {
        label: "User flow",
        caption: "Capture → analysis → ‘happy with the result?’ branch",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/36vt72jv_20260101_230844%201.png",
      },
    ],
    screens: [
      {
        label: "Home",
        caption: "Personal welcome + entry to the scan flow",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/1sah583c_image%204.png",
      },
      {
        label: "Scanner",
        caption: "Live capture with framing guidance",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/u2oh75z0_image%201.png",
      },
      {
        label: "Scan results",
        caption: "Per-rule verdicts + clear next action",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/zdpc061u_image%202.png",
      },
      {
        label: "Scan history",
        caption: "Memory layer that makes repeat decisions faster",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/9l38tmqu_image%203.png",
      },
    ],
    description:
      "An AI-powered mobile app that helps users instantly check whether packaged foods comply with dietary rules like Swaminarayan, Upvas, Vegan and Vegetarian — turning ingredient anxiety into a 3-second decision.",
    headline:
      "Turning ingredient-label doubt into a confident, in-store decision.",
    challenge:
      "Packaged food labels create uncertainty. Ingredient lists are dense and technical, and users hesitate at the moment of purchase exactly when they need confidence fast.",
    approach:
      "Designed a Scan → Understand → Decide flow with explainable AI verdicts — fast and low-effort for the in-store moment, reassuring instead of technical.",
    outcome:
      "Users move from uncertainty to clarity in seconds. Explainable results made the AI feel credible, and repeat scans started building mindful purchasing habits.",
    context:
      "SatvikScan lets users scan a packaged food label and instantly check whether it complies with their dietary principles — Swaminarayan, Upvas (fasting), Vegan or Vegetarian. The product has to win at the shelf, in seconds, without making the user feel like they’re using software. Buying decisions happen in moments — clarity, not data, is the actual product.",
    problem: [
      "Ingredient lists are dense and technical — hard to interpret in the few seconds users actually have to decide.",
      "Buying decisions happen in seconds. Users don’t want to research every ingredient on a packet.",
    ],
    process: [
      {
        title: "Defined the in-store moment",
        body: "Mapped the high-friction decision moment in real shopping environments. The product had to win in seconds, with one hand, mid-aisle — not on a sofa with time to read.",
      },
      {
        title: "Designed Scan → Understand → Decide",
        body: "A three-beat core flow that mirrors how people actually decide: see the product, get a clear verdict, act on it without re-reading the label. No more, no less.",
      },
      {
        title: "Made AI verdicts explainable",
        body: "Every result shows the why — which ingredients triggered the verdict, and how it ties back to the user’s chosen dietary rules. Confidence comes from reasoning, not a green tick.",
      },
      {
        title: "Built saved history into the loop",
        body: "Past scans become a memory layer. Repeat purchases get faster over time, and the app starts to feel like a habit rather than a tool.",
      },
    ],
    metrics: [
      { label: "Decision time", before: "minutes of doubt", after: "seconds of clarity" },
      { label: "Purchase confidence", before: "hesitation", after: "decided" },
      { label: "Repeat behaviour", before: "one-off use", after: "habit-forming" },
    ],
    learnings: [
      "Trust beats speed alone. Fast results matter, but believable results matter more.",
      "Clarity is emotional. Users aren’t just processing data — they’re looking for reassurance.",
      "Simplicity requires structure. The cleaner the experience feels, the stronger the system behind it has to be.",
    ],
  },
  {
    id: "angelpro",
    name: "AngelPro",
    category: "Fintech · Investing",
    tag: "Investment",
    year: "2025",
    role: "UX/UI Designer",
    duration: "3 months",
    team: "2 designers, 2 engineers, 1 PM",
    platform: "Web, iOS",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/tbgi4i9a_AngelPro%20Mockup%20Image.png",
    imagePosition: "center",
    screenAspect: "phone-framed",
    cardAspect: "phone",
    hideHero: true,
    projectOverview: {
      mission:
        "AngelPro was born from a simple observation: investors are busy, data is messy, and existing tools are clunky. Our mission was to deliver a mobile-first platform that provides ‘at-a-glance’ portfolio health — without sacrificing the depth of financial data required for serious decision-making.",
      personasHeading: "Primary users — who are we designing for?",
      personas: [
        {
          title: "Angel Investors",
          body: "The independent track-star — investing on their own, juggling deal flow on the side.",
        },
        {
          title: "Family Offices",
          body: "Managing multi-generational wealth with multiple members and shared visibility.",
        },
        {
          title: "Investment Groups",
          body: "Coordinating shared capital — pooled deals, structured allocations, group-level reporting.",
        },
      ],
    },
    description:
      "A mobile-first investment platform for angel investors and family offices — designed to make portfolio health, group ownership and deal intelligence feel obvious on a 6-inch screen.",
    headline:
      "A mobile-first platform to help angel investors and family offices track portfolios, manage documents, and understand performance metrics at a glance.",
    challenge:
      "Investors juggle deals across spreadsheets, emails, PDFs and folders — making portfolio health invisible until something breaks.",
    approach:
      "Three deliberate flows on one mobile spine: a clean Add-Investment journey, a Group-Investments flow with explicit ownership, and an AI assistant grounded in the user’s own deal documents.",
    outcome:
      "A unified mobile experience that replaces 5 tabs and a spreadsheet — investors can add a deal, split it across a group and ask the AI a plain-English question about it, all without leaving the app.",
    context:
      "Angel investors and family offices often manage investments across spreadsheets, emails, PDFs, and folders, creating a fragmented, time-consuming process that makes it difficult to get a clear picture of portfolio health.",
    problem: [
      "Fragmented Tracking — managing deals across multiple disconnected platforms leading to data silos.",
      "Inefficient Collaboration — friction in sharing critical updates and documents with partners or investment groups.",
      "Data Density on Mobile — condensing complex financial metrics into a readable and actionable mobile experience.",
    ],
    designGoals: [
      {
        title: "Glanceable Insights",
        body: "Optimising dense financial data for quick, high-level understanding on mobile screens.",
      },
      {
        title: "Ownership Clarity",
        body: "Providing absolute transparency in group deals by defining clear ownership and stake distribution.",
      },
      {
        title: "Lightweight MVP",
        body: "Designing a lightweight MVP focused only on core investor needs — nothing extra.",
      },
    ],
    process: [
      {
        title: "Mapped the three jobs an investor actually has",
        body: "Add a new deal · share a deal with a group · understand a deal you’ve already added. Every screen had to serve one of these three jobs — anything else got cut from the MVP.",
      },
      {
        title: "Designed the Add-Investment spine",
        body: "A four-step happy path — legal name, amount, documents, success — paired with a calm progress screen while the Deal Vault is being structured. Defaults handle 80% of the entry; advanced fields stay one tap away.",
      },
      {
        title: "Built Group Investments with ownership-first thinking",
        body: "From the moment a group is created, ownership is explicit — a live percentage allocator keeps the total at 100% and the group dashboard shows who put in how much, side by side with the company.",
      },
      {
        title: "Grounded the AI in the user’s own data",
        body: "The chat assistant never invents an answer — when the user doesn’t know what to ask, it offers 4 suggested prompts pulled directly from their Deal Vault (SHA, SPA, CCPS terms, post-exit shareholding).",
      },
      {
        title: "Designed for one-handed mobile use",
        body: "Primary CTAs sit in the thumb zone, dense financial data is broken into scannable cards, and every long form is one column with calm spacing — so the most important number is never more than a glance away.",
      },
    ],
    extraScreensTitle: "Adding an investment — the four-step happy path",
    extraScreensIntro:
      "The core flow every new investor sees first — a calm form to log a deal, an upload step that turns a folder of PDFs into a structured Deal Vault, and an explicit success moment.",
    extraScreensFlowLabel: "Flow 01 · Add Investment",
    extraScreensId: "angelpro-add-investment",
    extraScreensAspect: "phone-framed",
    extraScreens: [
      {
        label: "Add your first investment",
        caption:
          "A friendly form to log a new investment — investment name, amount in INR, and a smart legal-entity search that auto-suggests as the user types.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/v5czoz3p_S7.1_%20Add%20Investment%20Page%20For%20Legal%20Name.png",
      },
      {
        label: "Upload deal documents",
        caption:
          "Drop in SHAs, SPAs and share certificates — AngelPro auto-organises them into a Deal Vault the user can talk to, instead of digging through emails.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/iqw40z27_S8_%20Uplaod%20Documents%20page%20for%20Individual%20Investment.png",
      },
      {
        label: "Setting up the Deal Vault",
        caption:
          "A calm progress screen that tells the user exactly what’s happening — uploading, structuring, adding intelligence, securing — and invites them to start the next investment in parallel.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/s0c3d4d7_S9.1%20_%20Document%20processing%20Screen.png",
      },
      {
        label: "Investment added successfully",
        caption:
          "A clean success moment with two clear next steps — go to the dashboard, or add another investment.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/px21heeg_S9.3_%20Investment%20added%20success%20page.png",
      },
    ],
    extraFlows: [
      {
        title: "Group Investments — collaborate without losing individual clarity",
        intro:
          "A second flow that lets angel investors and family offices invest together, track who put in how much, and share a single, structured Deal Vault across the group.",
        flowLabel: "Flow 02 · Group Investments",
        screensId: "angelpro-group-investments",
        aspect: "phone-framed",
        screens: [
          {
            label: "Create a new group",
            caption:
              "Set up a group in seconds — name, type, description and the first set of members. Admins control who can manage members and investments.",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/dru7li1y_S18_%20Create%20New%20Group%20Page.png",
          },
          {
            label: "Tag an investment to the group",
            caption:
              "Pick an existing investment from a clean dropdown to associate it with the group — no need to re-enter any data.",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/wcapb78b_S20_%20Group%20Member%20Allocation.png",
          },
          {
            label: "Allocate by member %",
            caption:
              "Split the group investment by percentage across members. A live allocation summary keeps the total at 100% — no spreadsheet maths required.",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/trzw4go0_S20_%20Allocate%20Members.png",
          },
          {
            label: "Group created · associate an investment",
            caption:
              "A clear success moment with two paths — tag investments to this group, or skip and return to the main portfolio.",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/05y1om6g_S19_%20Associate%20Group%20with%20Investment.png",
          },
          {
            label: "Group portfolio dashboard",
            caption:
              "A single view for the entire group — invested capital, number of investments, each tagged company, and member status (Admin, Invited).",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/kynsze4d_S21_%20Group%20Portfolio%20Dashboard-1.png",
          },
        ],
      },
      {
        title: "AI Investment Assistant — answers in plain English, not jargon",
        intro:
          "A third flow that lets investors talk to their portfolio — ask a question, pick from suggested prompts, and get answers grounded in their own deal documents.",
        flowLabel: "Flow 03 · AI Assistant",
        screensId: "angelpro-ai-assistant",
        aspect: "phone-framed",
        screens: [
          {
            label: "Chat home",
            caption:
              "A friendly opener — ‘Hi, I’m your investment assistant. How can I help you today?’ — with a typing state that tells the user the bot is thinking.",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/go1tl60q_S16_%20Chat%20Page.png",
          },
          {
            label: "Suggested prompts",
            caption:
              "When users don’t know what to ask, the bot offers 4 grounded prompts — SHA rights, conversion terms, lock-in clauses, post-exit shareholding — drawn from their own Deal Vault.",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/nxe74jbh_S16_%20Chat%20with%20prompts.png",
          },
          {
            label: "Chat history",
            caption:
              "Every chat is saved and scannable — FinTech Sector Analysis, Q4 Portfolio Performance, Missing Documents Report — so investors can pick up where they left off.",
            url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/ra9sk6xy_S16_%20Chat%20History.png",
          },
        ],
      },
    ],
    metrics: [],
    outcomeHighlights: [
      "One mobile spine, three coherent flows",
      "Explicit % ownership in every group deal",
      "AI grounded in the investor’s own Deal Vault",
      "Lightweight MVP — only what investors need",
    ],
    learnings: [
      "Mobile fintech wins on glanceability — if the most important number isn’t visible in the first second, nothing else matters.",
      "AI feels trustworthy only when its answers are grounded in the user’s own documents — suggested prompts beat an empty chat box every time.",
    ],
  },
  {
    id: "qi-health",
    name: "Qi Health",
    category: "Health-tech",
    tag: "Healthcare",
    year: "2024–25",
    role: "UX/UI Designer",
    duration: "1 year",
    team: "Cross-functional · engineering, product",
    platform: "Digital care platform · Web",
    summary: "Health Platform",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/8fc19qiy_Qi%20Health%20thumbnail.jpg",
    imagePosition: "top",
    screenAspect: "laptop",
    heroPosition: "after-approach",
    myApproachPosition: "before-process",
    screens: [
      {
        label: "Programme Hero — Menopause",
        caption:
          "A calm, evidence-based landing for the Menopause programme — natural product photography paired with a single ‘Start Healing’ call to action sets the tone for the entire journey.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/k2awamp2_MacBook%20Pro%2016.png",
      },
      {
        label: "Personalised Dashboard",
        caption:
          "A welcoming patient home — Hi Nancy is greeted with a clear three-step healing journey (health form → tongue photos → book appointment), with her profile and current treatment plan pinned to the right.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/f26fwk0z_MacBook%20Pro%2019.png",
      },
      {
        label: "Human-centred Health Report",
        caption:
          "Diagnoses translated into approachable, editorial care plans — clinical insight without the cold tone.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/vevu9np8_MacBook%20Pro%2020.png",
      },
      {
        label: "Live Consultation",
        caption:
          "Video session paired with contextual tools — your herbs and symptom trends next to the doctor, not in another tab.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/6b6c0b1n_MacBook%20Pro%2022.png",
      },
      {
        label: "Herbal Prescriptions Library",
        caption:
          "A scannable herb library that helps patients understand the ingredients behind their personalised treatment plan — dense medical context turned into warm, legible cards.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/lzwr0x07_MacBook%20Pro%2023.png",
      },
    ],
    description:
      "A digital care platform that simplifies fragmented healthcare journeys through personalised wellness plans, consultations, herbal prescriptions and progress tracking — all in one cohesive ecosystem.",
    headline:
      "Bringing disconnected healthcare moments into one calm, cohesive ecosystem.",
    challenge:
      "Healthcare journeys were fragmented — discovery, consultation, prescriptions and follow-up all lived in disconnected places, leaving patients uncertain and unsupported between moments.",
    approach:
      "A guided, low-friction experience built on four principles: clarity over clutter, guidance over guesswork, trust through simplicity, and continuity over disconnected moments.",
    outcome:
      "A unified platform with a personalised dashboard, herbal prescription library, human-centred health reports and live consultations — all backed by a scalable design system built for growth.",
    context:
      "Qi Health is a digital care platform built to simplify fragmented healthcare journeys through personalised wellness plans, consultations, assessments and progress tracking. My role was to shape an experience that feels trustworthy, intuitive and human across every touchpoint — bringing disconnected healthcare moments into one ecosystem where users can discover programs, consult experts, track progress, and feel supported throughout their journey.",
    problem: [
      "Healthcare journeys felt fragmented — discovery, consultation, prescriptions and follow-up lived in disconnected places.",
    ],
    process: [
      {
        title: "Personalised Dashboard",
        body: "A centralised hub that helps patients track appointments, progress, plans and next steps. Designed to make the most important things easy to notice first, and to keep patients feeling supported between visits.",
      },
      {
        title: "Herbal Prescriptions library",
        body: "A visually guided herb library that helps patients understand the ingredients behind their personalised treatment plan — building trust in traditional medicine through transparency, with dense information turned into scannable cards.",
      },
      {
        title: "Human-centred Health Reports",
        body: "Transformed clinical insights into approachable care plans. Translated diagnoses into clear, warm language to reduce anxiety, structured dense medical insights into readable sections, and improved understanding of next steps.",
      },
      {
        title: "Live Consultations",
        body: "A consultation interface that combines video sessions with contextual health tools — creating calmer conversations, more informed decisions, and a stronger sense of being supported by your doctor.",
      },
    ],
    myApproach: {
      lead:
        "I focused on turning a complex healthcare ecosystem into a guided, low-friction experience.",
      principles: [
        "Clarity over clutter",
        "Guidance over guesswork",
        "Trust through simplicity",
        "Continuity over disconnected moments",
      ],
    },
    designSystem: {
      lead:
        "To maintain consistency across a growing platform, I created reusable UI components for:",
      items: [
        "Forms",
        "Cards",
        "Scheduling modules",
        "Health metrics",
        "Dashboards",
        "States & feedback",
      ],
    },
    metrics: [
      { label: "Care journey", before: "fragmented", after: "unified" },
      { label: "Patient experience", before: "clinical", after: "human-centred" },
      { label: "Treatment trust", before: "opaque", after: "transparent" },
      { label: "Design foundation", before: "ad-hoc", after: "scalable system" },
    ],
    learnings: [
      "Simplicity builds trust. The easier something feels, the safer it feels.",
      "Guidance matters. Especially in health products, users need reassurance — not just options.",
    ],
  },
];

// Showcase order — agent set 2026-02
export const caseStudies = [
  "sparkminds",
  "scijustice",
  "angelpro",
  "calmkraft",
  "satvikscan",
  "qi-health",
].map((id) => _caseStudiesRaw.find((c) => c.id === id));

// Personal / concept projects — side explorations Shweta runs on her own time
export const personalProjects = [
  {
    id: "tour-scheduler",
    name: "Tour Scheduler Pro",
    category: "Concept · Music ops",
    tag: "B2B Mobile",
    year: "2026",
    role: "Product Designer · self-initiated",
    duration: "Concept project",
    team: "Solo",
    platform: "iOS · light + dark",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/slywp87t_Events%20Dark%20Mode.png",
    imagePosition: "center",
    screenAspect: "phone-framed",
    cardAspect: "phone",
    screens: [
      {
        label: "Events · Dark mode",
        caption:
          "Your week, day by day. Each event is colour-coded — travel, rehearsal, show or day off — so you can read the whole tour in one glance.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/slywp87t_Events%20Dark%20Mode.png",
      },
      {
        label: "Events · Light mode",
        caption:
          "Same schedule, lighter theme. If two events clash, you’ll see a small ‘conflict detected’ note right on the day — no hunting for it.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/k1f4jf6a_Events%20Light%20Mode.png",
      },
      {
        label: "Propose New Event",
        caption:
          "Add a new gig in a few taps — name, date, venue, time zone. Then pick who to notify for approval.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/6kt6f8pa_Propose%20New%20Event%20Form.png",
      },
      {
        label: "Stakeholders & Conflicts",
        caption:
          "Everything you need in one place — your team, the events that clash, and a one-tap button to propose a new event.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/b8844d3s_Screen.png",
      },
    ],
    description:
      "A mobile-first tour scheduling concept for band managers — designed to keep travel, rehearsals and shows visible at a glance, with conflicts surfaced before they become problems.",
    headline: "",
    challenge:
      "Touring bands juggle managers, venues, crew, promoters and dozens of events. Most existing tools are calendar bolt-ons — built for everyone, optimised for no one.",
    approach:
      "A focused mobile app where the day-by-day schedule, conflict detection, stakeholder coordination and event proposals all live in one calm, theme-aware interface. Light and dark themes are designed as first-class peers — not just an afterthought toggle.",
    outcome:
      "A schedule that explains itself — colour-coded event types, status pills, conflict inline, and a primary CTA that puts the most common action (Propose Event) one tap away.",
    context:
      "Tour Scheduler Pro is a concept mobile app for tour managers — designed to make scheduling feel less like a spreadsheet and more like a daily companion. The product unifies events, stakeholders, conflicts and proposals into a single mobile surface, with deliberate light + dark themes for backstage and on-stage use.",
    problemLabel: "Project Overview",
    problem: [],
    designGoals: [],
    process: [
      {
        title: "Designed the day-by-day spine",
        body: "Every day is a card — date header, event blocks inside, colour-coded types and status pills. The user’s nervous system reads the week in one glance.",
      },
      {
        title: "Designed the proposal flow",
        body: "Propose New Event is the most common write action, so it gets a full-width primary button on the home screen. The form asks only what’s needed — title, dates, venue, time zone, stakeholders to notify.",
      },
    ],
    metrics: [],
    outcomeHighlights: [],
    learnings: [],
  },
  {
    id: "dating-app-settings",
    name: "Dating App · Settings",
    category: "Concept · Consumer mobile + web",
    tag: "Consumer mobile",
    year: "2026",
    role: "Product Designer · self-initiated",
    duration: "Concept project",
    team: "Solo",
    platform: "Web + iOS · responsive settings",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/octa2pgt_Profile%20Visibility%20and%20relationship%20intention%20settings.png",
    imagePosition: "center",
    screenAspect: "laptop",
    cardAspect: "laptop",
    screens: [
      {
        label: "Profile Visibility & Relationship Intention",
        caption:
          "Two settings — who can see me, and what am I here for — decision moments.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/octa2pgt_Profile%20Visibility%20and%20relationship%20intention%20settings.png",
        aspect: "laptop",
      },
      {
        label: "Family Preferences",
        caption:
          "‘Do you have children?’ and ‘Do you want children?’ surfaced as honest, low-pressure questions — with a warm ‘Family values matter here’ reassurance card below.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/epu5ff33_Family%20Preferences%20Settings.png",
        aspect: "laptop",
      },
      {
        label: "Privacy & Safety",
        caption:
          "The user manages safety features and privacy options to feel secure while using the platform.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/gzsxnb64_Privacy%20and%20safety%20settings.png",
        aspect: "laptop",
      },
      {
        label: "Communication & Account",
        caption:
          "The user updates notification preferences and account details to stay in control of their experience.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/ra6imbzy_Communication%20and%20Account%20Settings.png",
        aspect: "laptop",
      },
      {
        label: "Mobile view — Family Preferences",
        caption:
          "The same settings on iOS — same structure, same micro-copy, same privacy badge — so users carry one mental model between devices.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/j6egj67g_Mobile%20View.png",
        aspect: "phone-framed",
      },
    ],
    description:
      "A responsive settings system for a dating app — designed to make every privacy, intention and communication choice feel obvious, calm and trustworthy across web and mobile.",
    headline:
      "A comprehensive redesign of privacy and preference management for a high-intent dating application.",
    challenge:
      "Dating apps live or die on trust. The settings page is where users decide how safe they feel — and yet, it’s usually the most neglected screen in the product, packed with cryptic toggles and zero context.",
    approach:
      "Treated settings as a trust-building surface. Grouped controls by user intent (visibility, intention, family, privacy, comms, account) rather than by engineering domain. Paired each section with a soft contextual badge (‘Visible to matches’ / ‘Your data is encrypted’) and warm in-context reassurance cards.",
    outcome:
      "A responsive settings system where every control feels deliberate — same structure on web and iOS, same micro-copy, same privacy semantics. Destructive actions live where the user expects them, never where they don’t.",
    context:
      "A concept project exploring how a dating app’s settings can become a quiet, daily trust-building surface — not a place users dread visiting. Built around one principle: every toggle should answer a question the user is already asking in their head.",
    problemLabel: "Project Overview",
    problem: [],
    designGoals: [],
    process: [
      {
        title: "Listed questions a user asks",
        body: "‘Who can see me?’ ‘What am I here for?’ ‘Will my contacts find me?’ ‘What if I want out?’ Mapped each question to a setting before designing a single toggle.",
      },
      {
        title: "Designed the privacy primitives",
        body: "Toggles for screenshot blocking, contact-hiding and activity sharing. Explicit Manage / Request actions for blocked users and data export.",
      },
      {
        title: "Made destructive actions safe-by-default",
        body: "Sign out and Delete live in a dedicated Account card, with red-tinted secondary styling. They’re findable, but never accidental.",
      },
    ],
    metrics: [],
    outcomeHighlights: [],
    learnings: [],
  },
  {
    id: "task-manager",
    name: "AI Task Manager",
    category: "Concept · Productivity AI",
    tag: "Personal · Mobile",
    year: "2025",
    role: "Product Designer · self-initiated",
    duration: "Concept project",
    team: "Solo",
    platform: "iOS · mobile-first concept",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/ocgu5660_Steps%20Suggested.png",
    imagePosition: "center",
    screenAspect: "phone-framed",
    cardAspect: "phone",
    screens: [
      {
        label: "Create new task",
        caption:
          "A friendly, single-column intake — title, priority, tags, optional description and a clear ‘Break into Steps’ moment that hands the heavy lifting to the AI.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/sgjd6j74_Create%20New%20Task.png",
      },
      {
        label: "Thinking through this…",
        caption:
          "An honest loading state — the AI says exactly what it’s doing, so the wait feels intentional rather than broken.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/xw6bbgng_Suggesting%20Steps.png",
      },
      {
        label: "Suggested steps",
        caption:
          "AI-generated subtasks the user can edit, reorder, delete or add to — the model proposes, the human disposes.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/ocgu5660_Steps%20Suggested.png",
      },
      {
        label: "Task details",
        caption:
          "Priority, due date, tags, description, sub-tasks, reminder and an overflow for duplicate / share / delete — everything you need on one calm screen.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/0lkiw7x0_Task%20Details.png",
      },
      {
        label: "Task completed",
        caption:
          "A small ‘Great Job! 🎉’ toast and a celebratory bar — completion gets a moment, not just a checkmark.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/jra0b6dk_Task%20Completed.png",
      },
    ],
    description:
      "A mobile concept that pairs a calm task manager with an AI step-breaker — write the goal, let the model propose the path, then own the rest.",
    headline:
      "Turning ‘write a paper on coastal cities’ into five small wins.",
    challenge:
      "Most task apps assume the user already knows how to break a goal down. For students, freelancers and anyone with a heavy todo list, the actual blocker isn’t writing tasks — it’s knowing what the next concrete step should be.",
    approach:
      "A focused intake (title · priority · tags · description), an explicit ‘Break into Steps’ AI moment, and an editable subtask list — pairing model intelligence with human agency at every step.",
    outcome:
      "A concept flow where AI removes the scariest part of starting (‘what do I even do first?’) without taking away control, ownership or the small rituals — celebration toasts, reminders, soft-coloured tags — that make a task app feel personal.",
    context:
      "AI Task Manager is a mobile-first concept that explores how generative AI can sit inside a personal todo app — not as a chat sidebar, but as a single, well-placed action that turns vague goals into clear, editable steps.",
    problemLabel: "Project Overview",
    problem: [
      "Writing a task is easy. Knowing how to start it is the actual problem — most users stall on goals like ‘research a topic’ because there’s no obvious first step.",
      "Most AI productivity tools live in a separate chat tab — a context-switch the user has to opt in to. The model needs to live inside the flow, not outside it.",
    ],
    designGoals: [
      {
        title: "AI in the flow, not in a sidebar",
        body: "‘Break into Steps’ sits exactly where the user finishes describing the task — one tap turns a goal into a draft plan.",
      },
      {
        title: "Human stays in control",
        body: "Every AI-suggested step is editable, reorderable and deletable. Users can also append their own. The model proposes, the human disposes.",
      },
      {
        title: "Honest loading, soft delight",
        body: "‘Thinking through this…’ tells users exactly what’s happening; ‘Great Job 🎉’ rewards completion. Tone-of-voice is the smallest, biggest part of the system.",
      },
      {
        title: "Calm, scannable density",
        body: "Soft greens and warm neutrals; rounded chips for priority, date, tags; a single column that never feels cramped on a one-handed grip.",
      },
    ],
    process: [
      {
        title: "Mapped the ‘blank page’ moment",
        body: "Identified the highest-friction beat in any productivity app: the user knows the goal but doesn’t know the first step. That moment became the product’s core opportunity.",
      },
      {
        title: "Designed a single-column intake",
        body: "Title, priority, tags, optional description — paired with a clearly labelled ‘Break into Steps’ button.",
      },
      {
        title: "Made the AI moment legible",
        body: "An explicit ‘Thinking through this…’ loading state with a calm spinner. Then an editable list of suggested steps, with drag-handles, numbers, delete-X and an ‘Add Another Step’ field at the bottom.",
      },
      {
        title: "Designed the full task lifecycle",
        body: "Task details with priority, due date, tags, description, sub-tasks, reminder toggle and an overflow menu (duplicate · share · delete). Completion is celebrated with a strikethrough title, a green progress bar and a ‘Great Job 🎉’ toast.",
      },
    ],
    metrics: [],
    outcomeHighlights: [
      "AI ‘Break into Steps’ in the flow",
      "Fully editable subtask list",
      "Honest loading + soft completion delight",
      "One-screen task details",
    ],
    learnings: [
      "AI is most useful when it removes the blank page — not when it tries to remove the user’s judgment.",
      "Tone-of-voice (‘Thinking through this…’, ‘Great Job 🎉’) is the cheapest, most underrated way to make an AI feature feel trustworthy.",
      "Soft greens, rounded chips and a single-column rhythm make a task app feel personal.",
    ],
  },
  {
    id: "ims",
    name: "IMS · Inventory Management",
    category: "Concept · B2B SaaS",
    tag: "B2B SaaS",
    year: "2025",
    role: "Product Designer · self-initiated",
    duration: "Concept project",
    team: "Solo",
    platform: "Web · partner-store dashboard",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/2yziivg6_image%201.png",
    imagePosition: "center",
    screenAspect: "laptop",
    cardAspect: "laptop",
    screens: [
      {
        label: "Inventory overview",
        caption:
          "A four-card snapshot — total · in stock · low stock · out of stock — answers ‘how’s the shop today?’ before any clicking.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/2yziivg6_image%201.png",
      },
      {
        label: "Single canonical table",
        caption:
          "Brand · Quantity · Status · Selling Price · MRP · Actions — one table, soft status pills, generous spacing.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/ik44ikx7_image%202.png",
      },
      {
        label: "Sort by category",
        caption:
          "Personal Care, Snacks, Beverages, Dairy — a single dropdown narrows the table without any extra navigation.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/u4bsp5t4_image%204.png",
      },
      {
        label: "Per-row actions",
        caption:
          "Update Quantity and View Details live where the data lives — no detours into separate edit screens.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/9ksr1yn3_image%203.png",
      },
      {
        label: "Sort by status",
        caption:
          "All status · In stock · Low stock · Out of stock — the urgent rows are one click away.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/x7cj4bza_image%205.png",
      },
    ],
    description:
      "A SaaS-style inventory dashboard for partner stores — designed to make stock decisions feel like reading a sentence, not parsing a spreadsheet.",
    headline: "",
    challenge:
      "Partner stores manage their inventory across paper notebooks, WhatsApp messages and spreadsheets. The brand needed a single dashboard a store owner could open and immediately know what’s in stock, what’s running low, and what to reorder.",
    approach:
      "A focused four-card snapshot at the top, a single canonical product table beneath it, and progressively-disclosed actions per row — the dashboard reads as ‘states first, details on demand’.",
    outcome:
      "A clean, scannable inventory dashboard that any store owner can navigate without training — stock states, search, multi-axis sorting and per-row actions in one screen.",
    context:
      "IMS is a partner-store inventory dashboard concept — designed for general store owners who need to see stock health, manage products, and act on them without an analyst’s mindset. Built around one principle: the most important state should be visible before any interaction.",
    problemLabel: "Project Overview",
    problem: [
      "Partner stores often track inventory across paper notebooks, WhatsApp and spreadsheets — making stock health invisible until a customer asks for a missing item.",
      "Owners need to evaluate stock state, search by product, filter by category and status, and act on individual rows — without context-switching.",
    ],
    designGoals: [
      {
        title: "Stock-state at a glance",
        body: "Surface total · in stock · low stock · out of stock as the first thing any owner sees on opening the dashboard.",
      },
      {
        title: "Find anything in seconds",
        body: "Search and three independent sort axes (category, status, brand) work together so the right row is one click away.",
      },
      {
        title: "Act, don’t navigate",
        body: "Per-row actions like Update Quantity and View Details live where the data lives — no detours into separate edit screens.",
      },
      {
        title: "Friendly density",
        body: "The grid stays calm even with dozens of rows — generous spacing, soft status pills, and a palette that telegraphs urgency without alarm.",
      },
    ],
    process: [
      {
        title: "Mapped the operator’s day",
        body: "Designed for a busy store owner — not a back-office operator. The dashboard had to win in 6 seconds before they got pulled away by a customer.",
      },
      {
        title: "Designed the four-card snapshot",
        body: "Total products · In Stock · Low Stock · Out of Stock as a first-row summary — colour-coded so the urgent number is also the most visible.",
      },
      {
        title: "Built one canonical table",
        body: "Product name, SKU, brand, quantity, status, selling price, MRP, actions — every column earns its place. No tabs, no sub-views — one table, sortable on the columns that matter.",
      },
      {
        title: "Surfaced actions where the data lives",
        body: "An overflow menu per row keeps the table calm but makes Update Quantity and View Details a single click away — no modal-stacks to recover from.",
      },
    ],
    metrics: [],
    outcomeHighlights: [
      "Stock-state snapshot above the fold",
      "Multi-axis sorting (category · status · brand)",
      "Per-row Update / View actions",
      "Owner-first, training-free UI",
    ],
    learnings: [
      "Operators don’t read tables — they scan for the colour they’re worried about. Design for the scan, not for completeness.",
      "Defaults beat configurability for non-technical users. The dashboard should already be sorted the way they’d have sorted it.",
      "A single, canonical table almost always beats three small ones — context-switching is the real cost.",
    ],
  },
  {
    id: "dogwalker",
    name: "Dog Walker Profile",
    category: "Concept · Mobile product",
    tag: "Personal · Mobile",
    year: "2025",
    role: "Product Designer · self-initiated",
    duration: "Weekend concept",
    team: "Solo",
    platform: "iOS · mobile-first concept",
    summary: "",
    image:
      "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/9sljxl8m_image%202.png",
    imagePosition: "center",
    screenAspect: "phone-framed",
    screens: [
      {
        label: "Header & Personal Introduction",
        caption:
          "A warm, human bio helps users understand personality, care style and communication habits.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/mue6kxg9_image%201.png",
      },
      {
        label: "Services & Pricing",
        caption:
          "Transparent service cards with pricing and package discounts — clarity over guessing.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/9sljxl8m_image%202.png",
      },
      {
        label: "Skills & Expertise",
        caption:
          "Structured tags help users evaluate fit by dog size, temperament, breeds and behaviour comfort.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/t2fvaatn_image%203.png",
      },
      {
        label: "Weekly Availability & Certifications",
        caption:
          "Users can quickly assess schedule fit across mornings, afternoons and evenings.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/sqwdlbx9_image%204.png",
      },
      {
        label: "Client Reviews",
        caption:
          "Rich reviews with visible ratings create trust through real owner experiences.",
        url: "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/v67a5829_image%205.png",
      },
    ],
    description:
      "A mobile-first concept exploring how a dog walker profile page can feel warm, credible and conversion-ready — reimagined as a decision-making tool, not just a listing.",
    headline:
      "Making the ‘can I trust you with my dog?’ moment feel obvious.",
    challenge:
      "Hiring someone to care for a pet is emotional. Users aren’t just booking a service — they’re choosing someone to trust with a family member. Most profile pages don’t answer the real questions fast enough.",
    approach:
      "A mobile-first profile designed around the six questions every pet owner silently asks — trust, experience, fit, availability, price and social proof — answered with structure, warmth and progressive disclosure.",
    outcome: "",
    context:
      "A mobile Dog Walker profile page concept designed to help pet owners confidently choose and book a trusted dog walker. The experience focuses on enabling quick decision-making by surfacing key information like trust, availability, pricing, and expertise in a clear and intuitive way. The visual design uses soft greens and warm neutrals to convey care, safety, and a sense of calm professionalism.",
    problem: [
      "Hiring a pet caregiver is emotional — users hesitate because there isn’t enough trust surfaced up front.",
      "Availability, pricing and expertise are often buried — users can’t evaluate fit without digging through screens.",
    ],
    process: [
      {
        title: "Header & personal introduction",
        body: "The hero surfaces everything a nervous pet owner wants first — a real face, location, star rating, verified badges, years of experience, walks completed, and a warm human bio. Credibility before anything else.",
      },
      {
        title: "Services & transparent pricing",
        body: "Clear cards for 30-min walk, 60-min adventure, doggy daycare and overnight stay — each with a plain-language description, duration and price. Package discounts sit right beneath, so buyers see the upside without negotiating.",
      },
      {
        title: "Availability & certifications",
        body: "A weekly grid shows morning / afternoon / evening availability at a glance — no calendar diving. Beneath it, Pet First Aid & CPR, Professional Dog Walking and Behavior Specialist certifications reinforce safety and skill.",
      },
      {
        title: "Skills, expertise & service areas",
        body: "Structured tags let users filter by size preference, temperament comfort, breeds worked with and service areas. Compatibility becomes scannable — not a paragraph to parse.",
      },
      {
        title: "Reviews & persistent CTAs",
        body: "Rich, star-rated reviews with the reviewer’s dog’s name create emotional social proof. Message and Book Now buttons stay pinned throughout — the decision and the action never drift apart.",
      },
    ],
    metrics: [],
    learnings: [],
  },
];

// Additional projects revealed via "Load more projects" — currently empty
export const moreProjects = [];

// 6-step process
export const processSteps = [
  {
    step: "01",
    title: "Understanding The Users & Problem",
    questions: [
      "Who are we designing for?",
      "What is the business trying to achieve?",
      "What constraints exist (tech, timeline, budget)?",
    ],
    body: "I begin by aligning with both user needs and business goals. This includes stakeholder discussions, reviewing existing products, and identifying constraints early.",
  },
  {
    step: "02",
    title: "Finding Clarity",
    questions: ["What is the core problem?"],
    body: "Next, pulling together research insights to create a core problem statement and defining the North Star for the project’s success.",
  },
  {
    step: "03",
    title: "Ideate",
    questions: ["How can we solve it?"],
    body: "Then, we brainstorm a wide spectrum of solutions using methods like Crazy 8s, storyboarding and find creative ways to solve what users really need.",
  },
  {
    step: "04",
    title: "Making Ideas Tangible",
    questions: ["How might this work?", "Wireframes → flows → prototypes"],
    body: "Next, I turn ideas into interactive prototypes — making it easier for teams and stakeholders to react, validate, and refine.",
  },
  {
    step: "05",
    title: "Test",
    questions: ["Does it actually work?"],
    body: "Then, we review and refine ideas with clients and stakeholders and later test with real users to see what’s not working and improve the experience.",
  },
  {
    step: "06",
    title: "Polish, Deliver & Evolve",
    questions: [
      "How can we make it better?",
      "Handoff to developers",
      "Post-launch thinking",
    ],
    body: "And, I ensure designs are implementation-ready during handoff. We keep making small improvements based on what we learn, so the product keeps getting better.",
  },
];

export const testimonials = [
  {
    quote:
      "Shweta is the rare designer who can hold the entire product in her head — model behaviour, edge cases, business goals — and still ship craft. She made our AI feel obvious.",
    name: "Aarav Mehta",
    role: "Co-founder & CEO",
    company: "SparkMinds",
  },
  {
    quote:
      "She rewrote the way our investor onboarding worked. Activation almost doubled in a quarter. We hire designers, but we trust Shweta with the hard problems.",
    name: "Priya Sharma",
    role: "Head of Product",
    company: "AngelPro",
  },
  {
    quote:
      "Shweta took a fragmented healthcare journey and made it feel like one calm, human experience. Patients now move from discovery to consultation to follow-up without ever feeling lost. Genuinely senior thinking.",
    name: "Dr. Rohan Iyer",
    role: "Chief Medical Officer",
    company: "Qi Health",
  },
];

export const aboutMeta = {
  yearsExperience: "4+",
  productsShipped: "20+",
  domainsCovered: "5",
  // Replace with Shweta's own portrait when available
  profileImage:
    "https://customer-assets.emergentagent.com/job_shweta-design/artifacts/wuy3z0ef_Profile%20photo%20.jpg",
  intro: "And this is me,",
  firstName: "Shweta",
  lastName: "Chauhan",
  title: "I’m a Product Designer",
  bio: "I am working at the intersection of AI, product thinking, and clarity — designing digital products that make complex systems feel simple, guided, and human.",
  certificate: "Google UX Certificate",
  certificateHref:
    "https://www.credly.com/badges/fec5b3f2-4174-41da-8ad6-b5801fa492ed/public_url",
  journeyHref: "#about",
};

export const contactLinks = {
  email: "shwetachauhan1011@gmail.com",
  linkedin: "https://www.linkedin.com/in/shweta-chauhan-5b5a41214",
  resume:
    "https://docs.google.com/document/d/17Ku96GRVRpnYb3M23n0yU75BiFCRQ6zTuDNsB18n9q8/export?format=pdf",
};
